package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.data.persistClientPhoto
import org.gymly.model.AdminClient
import org.gymly.model.AdminClientStatus
import org.gymly.model.AdminClientUpsert
import org.gymly.model.AdminBillingPeriod
import org.gymly.model.AdminMembershipOrderStatus
import org.gymly.model.AdminMembershipOrderUpsert
import org.gymly.model.AdminMembershipPlanItem
import org.gymly.model.AdminPaymentCollect
import org.gymly.model.AdminPaymentCollectTarget
import org.gymly.model.AdminPaymentDiscount
import org.gymly.model.AdminPaymentMethod
import org.gymly.model.AdminPersonSex
import org.gymly.model.AdminTrainer
import org.gymly.model.PickedImagePayload
import org.gymly.model.selectableForOrders
import org.gymly.theme.AdminColors
import org.gymly.theme.GymlySpace
import org.gymly.util.GymlyCurrency
import org.gymly.util.formatMoney
import org.gymly.util.openImagePicker
import org.gymly.util.parseFlexibleDateToIso
import org.gymly.util.todayIsoDate as bulkFormTodayIsoDate
import org.gymly.util.userMessage
import org.gymly.ui.components.VerticalScrollColumn
import kotlin.time.TimeSource

private enum class BulkSignupMode {
    Enroll,
    Payment,
}

private data class EnrollProcessOutcome(
    val rowId: String,
    val title: String,
    val subtitle: String?,
    val ok: Boolean,
    val clientId: String?,
    val orderId: String?,
    val amountCents: Long,
    val amountInput: String,
    val isCourtesy: Boolean,
    val paymentConfigured: Boolean,
    val paymentLines: List<PaymentLineDraft>,
    val changeGivenCents: Long = 0L,
    val changeGivenUsdCents: Long = 0L,
    val pendingUsdCents: Long = 0L,
    val pendingBsCents: Long = 0L,
    val discount: AdminPaymentDiscount? = null,
)

private fun preferredWeeklyPlan(plans: List<AdminMembershipPlanItem>): AdminMembershipPlanItem? =
    plans.firstOrNull { it.billingPeriod == AdminBillingPeriod.WEEKLY && !it.isPeriodOnlyLegacyName() }
        ?: plans.firstOrNull { it.billingPeriod == AdminBillingPeriod.WEEKLY }
        ?: plans.firstOrNull()

private fun planDefaultAmount(plans: List<AdminMembershipPlanItem>, planId: String?): String =
    plans.firstOrNull { it.id == planId }?.let { (it.priceCents / 100.0).toString() } ?: ""

private fun planDefaultUsdAmount(plans: List<AdminMembershipPlanItem>, planId: String?): String =
    plans.firstOrNull { it.id == planId }?.priceUsdCents?.takeIf { it > 0 }?.let { centsToAmountInput(it) }.orEmpty()

private fun newBulkMemberRow(
    plans: List<AdminMembershipPlanItem>,
    defaultPlanId: String?,
): BulkMemberRow {
    val planId = defaultPlanId ?: preferredWeeklyPlan(plans)?.id
    return BulkMemberRow(
        id = kotlin.time.Clock.System.now().toString(),
        planId = planId,
        paidAt = bulkFormTodayIsoDate(),
        startsAt = bulkFormTodayIsoDate(),
        endsAt = planId?.let { id ->
            plans.firstOrNull { it.id == id }?.let { planEndsAtFromStart(bulkFormTodayIsoDate(), it.billingPeriod) }
        }.orEmpty(),
        amountOverride = planDefaultAmount(plans, planId),
    )
}

@Composable
fun AdminBulkSignupScreen() {
    val missingBulkPermissions = bulkMissingPermissions()
    val scope = rememberCoroutineScope()
    val pendingApprovals = LocalPendingApprovalNotifier.current
    var loadingMeta by remember { mutableStateOf(true) }
    var processing by remember { mutableStateOf(false) }
    var progress by remember { mutableStateOf(0 to 0) }
    var error by remember { mutableStateOf<String?>(null) }
    var bulkTerminalRowIndex by remember { mutableStateOf<Int?>(null) }
    var paymentDialogRowIndex by remember { mutableStateOf<Int?>(null) }
    var processResults by remember { mutableStateOf<List<BulkProcessResultItem>?>(null) }
    var processSummary by remember { mutableStateOf("") }

    var companyId by remember { mutableStateOf<String?>(null) }
    var plans by remember { mutableStateOf<List<AdminMembershipPlanItem>>(emptyList()) }
    var trainers by remember { mutableStateOf<List<AdminTrainer>>(emptyList()) }
    var existingClients by remember { mutableStateOf<List<AdminClient>>(emptyList()) }

    var mode by remember { mutableStateOf(BulkSignupMode.Enroll) }
    val enrollMembership = mode == BulkSignupMode.Enroll
    var defaultPlanId by remember { mutableStateOf<String?>(null) }

    val rows = remember {
        mutableStateListOf(
            BulkMemberRow(id = "1"),
            BulkMemberRow(id = "2"),
            BulkMemberRow(id = "3"),
        )
    }

    fun resetEnrollRows() {
        rows.clear()
        repeat(3) { rows.add(newBulkMemberRow(plans, defaultPlanId)) }
    }

    fun keepOnlyFailedEnrollRows(results: List<BulkProcessResultItem>) {
        val keepIds = results.filter { it.keepForCorrection }.mapNotNull { it.rowId }.toSet()
        val kept = rows
            .filter { it.id in keepIds }
            .map { it.copy(ok = null, result = null) }
        rows.clear()
        if (kept.isEmpty()) {
            rows.add(newBulkMemberRow(plans, defaultPlanId))
        } else {
            rows.addAll(kept)
        }
    }

    fun reloadMeta() {
        scope.launch {
            loadingMeta = true
            error = null
            try {
                companyId = runCatching { AdminRepo.api.getCompany().id }.getOrElse {
                    AdminRepo.api.listBranches().firstOrNull()?.id
                }
                plans = AdminRepo.api.listMembershipPlans().selectableForOrders()
                trainers = runCatching { AdminRepo.api.listTrainers() }.getOrElse {
                    AdminRepo.api.listWorkers()
                        .filter {
                            it.role == org.gymly.model.AdminStaffRole.TRAINER ||
                                it.role == org.gymly.model.AdminStaffRole.ADMIN
                        }
                        .map { AdminTrainer(it.id, it.displayName, it.email, it.role.name) }
                }
                existingClients = runCatching { AdminRepo.api.listClients().forClientPicker() }.getOrElse { emptyList() }
                val defPlan = preferredWeeklyPlan(plans)?.id
                if (defaultPlanId == null) defaultPlanId = defPlan
                rows.forEachIndexed { index, row ->
                    if (row.planId == null || row.startsAt.isBlank() || row.endsAt.isBlank() || row.paidAt.isBlank()) {
                        val planId = row.planId ?: defPlan
                        val start = row.startsAt.ifBlank { bulkFormTodayIsoDate() }
                        val plan = plans.firstOrNull { it.id == planId }
                        rows[index] = row.copy(
                            planId = planId,
                            paidAt = row.paidAt.ifBlank { bulkFormTodayIsoDate() },
                            startsAt = start,
                            endsAt = row.endsAt.ifBlank {
                                plan?.let { planEndsAtFromStart(start, it.billingPeriod) }.orEmpty()
                            },
                            amountOverride = row.amountOverride.ifBlank { planDefaultAmount(plans, planId) },
                        )
                    }
                }
            } catch (e: Throwable) {
                error = e.userMessage("No se pudieron cargar planes/entrenadores.")
            } finally {
                loadingMeta = false
            }
        }
    }

    LaunchedEffect(Unit) { reloadMeta() }

    val validRows = rows.count { rowHasPersonData(it) }
    val totalCents = if (enrollMembership) {
        rows.sumOf { row ->
            if (!rowHasPersonData(row) || row.kind != BulkEnrollKind.MEMBERSHIP || !row.paymentConfigured) 0L
            else when (row.paymentStatus) {
                AdminMembershipOrderStatus.COURTESY -> 0L
                else -> parseBulkAmountToCents(
                    row.amountOverride.ifBlank { planDefaultAmount(plans, row.planId) },
                )
            }
        }
    } else {
        0L
    }

    BoxWithConstraints(
        Modifier
            .fillMaxSize()
            .background(AdminColors.Surface),
    ) {
        val compact = maxWidth < 700.dp
        Column(
            Modifier
                .fillMaxSize()
                .padding(if (compact) GymlySpace.md else GymlySpace.lg),
        ) {
            if (compact) {
                Column(Modifier.fillMaxWidth()) {
                    Text("Alta masiva", fontSize = 22.sp, fontWeight = FontWeight.Bold, color = AdminColors.Text)
                    Text(
                        if (mode == BulkSignupMode.Payment) {
                            "Cobro de membresía a socios existentes: busca, agrega a la cola y registra."
                        } else {
                            "Inscripción de nuevos socios: foto primero, datos amplios y cobro combinado por persona."
                        },
                        color = AdminColors.TextMuted,
                        fontSize = 13.sp,
                        modifier = Modifier.padding(top = 4.dp),
                    )
                }
            } else {
                Row(
                    Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.Top,
                ) {
                    Column(Modifier.weight(1f)) {
                        Text("Alta masiva", fontSize = 24.sp, fontWeight = FontWeight.Bold, color = AdminColors.Text)
                        Text(
                            if (mode == BulkSignupMode.Payment) {
                                "Cobro de membresía a socios existentes: busca, agrega a la cola y registra."
                            } else {
                                "Inscripción de nuevos socios: foto primero, datos amplios y cobro combinado por persona."
                            },
                            color = AdminColors.TextMuted,
                            fontSize = 13.sp,
                            modifier = Modifier.padding(top = 4.dp),
                        )
                    }
                }
            }
            if (enrollMembership) {
                if (compact) Spacer(Modifier.height(10.dp))
                Button(
                    onClick = {
                        if (processing) return@Button
                        error = null
                        val toProcess = rows.filter { rowHasPersonData(it) }
                        if (toProcess.isEmpty()) {
                            error = "Agrega al menos una fila con nombre y apellido, o correo de un socio existente."
                            return@Button
                        }
                        if (toProcess.any { it.kind != BulkEnrollKind.RECORD_ONLY && it.planId.isNullOrBlank() }) {
                            error = "Cada persona con membresía o cortesía debe tener un plan."
                            return@Button
                        }
                        val periodConflicts = toProcess.filter {
                            it.kind != BulkEnrollKind.RECORD_ONLY && !it.periodConflictMsg.isNullOrBlank()
                        }
                        if (periodConflicts.isNotEmpty()) {
                            error = "Hay ${periodConflicts.size} persona(s) con plan/periodo duplicado. " +
                                "Corrige plan o fecha de inicio antes de procesar."
                            return@Button
                        }
                        // Cada persona se cobra por separado al procesar: no hay validación de cobertura de toda la planilla.
                        // Persona nueva con un documento que ya es de un socio: no se crea de nuevo.
                        toProcess.forEach { row ->
                            val doc = row.docId.trim()
                            if (row.linkedClientId == null && doc.isNotEmpty()) {
                                val dup = existingClients.firstOrNull { it.docId?.trim().equals(doc, ignoreCase = true) }
                                if (dup != null) {
                                    error = "${row.firstName} ${row.lastName}: el documento $doc ya es de ${dup.firstName} ${dup.lastName}. " +
                                        "Elige ese socio en la fila o corrige el documento."
                                    return@Button
                                }
                            }
                        }
                        scope.launch {
                                processing = true
                                progress = 0 to toProcess.size
                                val clock = TimeSource.Monotonic
                                val started = clock.markNow()
                                var msResolveCreate = 0L
                                var msPhoto = 0L
                                var msOrders = 0L
                                var msPayments = 0L
                                val outcomes = mutableListOf<EnrollProcessOutcome>()
                                val accessSyncIds = linkedSetOf<String>()
                                // Cotización para convertir líneas en divisas; la usa el cobro de cada fila.
                                val exchangeQuote = runCatching { AdminRepo.api.currentExchangeRate() }.getOrNull()
                                // Usa cache local (ya cargada); evita re-listar todos los clientes en prod.
                                if (existingClients.isEmpty()) {
                                    existingClients = runCatching {
                                        AdminRepo.api.listClients().forClientPicker()
                                    }.getOrElse { emptyList() }
                                }
                                toProcess.forEachIndexed { index, row ->
                                    val idx = rows.indexOfFirst { it.id == row.id }
                                    val amountInput = row.amountOverride.ifBlank { planDefaultAmount(plans, row.planId) }
                                    // Cortesía = plan de cortesía (sin cobro).
                                    val isCourtesy = plans.firstOrNull { it.id == row.planId }?.billingPeriod ==
                                        AdminBillingPeriod.COURTESY
                                    val amountCents = if (isCourtesy) {
                                        0L
                                    } else {
                                        parseBulkAmountToCents(amountInput)
                                    }
                                    val personLabel = listOf(row.firstName, row.lastName)
                                        .map { it.trim() }
                                        .filter { it.isNotEmpty() }
                                        .joinToString(" ")
                                        .ifBlank { row.email.ifBlank { "Persona ${index + 1}" } }
                                    // Cobro de esta persona: se valida antes de crear nada. Si no cuadra, no se crea cliente ni orden.
                                    val rowNeedsCollect = !isCourtesy && row.paymentConfigured && row.paymentLines.isNotEmpty() &&
                                        (bulkDueBsCents(amountCents, row.pendingBsCents) > 0L || row.paymentLines.any { it.method.usesUsdAmount() })
                                    if (rowNeedsCollect) {
                                        val (_, rowCheckLines) = prepareBulkCollectLines(
                                            entries = listOf(amountInput to row.paymentLines),
                                            fallbackAmountInput = amountInput,
                                            quote = null,
                                        )
                                        val rowDueBsNet = bulkNetDueBsCents(amountCents, row.pendingBsCents, row.discount?.cents ?: 0L)
                                        // Excedente en Bs sin vuelto declarado (mismo criterio que Ventas y Órdenes).
                                        val rowBsLines = row.paymentLines.filterNot { it.method.usesUsdAmount() }
                                            .sumOf { parseBulkAmountToCents(it.amountInput) }
                                        val rowBsExcess = rowBsLines - rowDueBsNet
                                        val rowUndeclared = if (rowBsExcess > 0L) rowBsExcess - minOf(row.changeGivenCents, rowBsExcess) else 0L
                                        val rowCheckMsg = if (rowUndeclared > maxOf(100L, rowDueBsNet / 200L)) {
                                            "El cobro supera lo debido en Bs " +
                                                formatMoney(rowUndeclared.toInt(), GymlyCurrency.CODE) +
                                                " y no tiene vuelto declarado. Corrige el monto o declara el vuelto."
                                        } else {
                                            validateBulkCollectCoverage(rowDueBsNet, rowCheckLines)
                                        }
                                        if (rowCheckMsg != null) {
                                            if (idx >= 0) {
                                                rows[idx] = rows[idx].copy(ok = false, result = rowCheckMsg)
                                            }
                                            outcomes += EnrollProcessOutcome(
                                                rowId = row.id,
                                                title = personLabel,
                                                subtitle = rowCheckMsg,
                                                ok = false,
                                                clientId = null,
                                                orderId = null,
                                                amountCents = amountCents,
                                                amountInput = amountInput,
                                                isCourtesy = isCourtesy,
                                                paymentConfigured = false,
                                                paymentLines = emptyList(),
                                            )
                                            progress = (index + 1) to toProcess.size
                                            return@forEachIndexed
                                        }
                                    }
                                    try {
                                        val tResolve = clock.markNow()
                                        val resolved = resolveOrCreateClient(
                                            row = row,
                                            existing = existingClients,
                                            companyId = companyId,
                                            allowCreate = true,
                                        )
                                        msResolveCreate += tResolve.elapsedNow().inWholeMilliseconds
                                        val client = resolved.client
                                        if (resolved.created) {
                                            existingClients = existingClients + client
                                        }
                                        // Cliente ya creado: si la fila falla después, al corregir se usa este mismo socio.
                                        if (idx >= 0) {
                                            rows[idx] = rows[idx].copy(linkedClientId = client.id)
                                        }
                                        // Foto sí; sync Hikvision al final (es lo más lento en producción).
                                        if (row.photo != null) {
                                            val tPhoto = clock.markNow()
                                            runCatching {
                                                AdminRepo.api.persistClientPhoto(client.id, photo = row.photo)
                                            }
                                            msPhoto += tPhoto.elapsedNow().inWholeMilliseconds
                                            accessSyncIds += client.id
                                        } else if (resolved.created) {
                                            accessSyncIds += client.id
                                        }
                                        if (row.kind == BulkEnrollKind.RECORD_ONLY) {
                                            val label = buildString {
                                                append(if (resolved.created) "Nuevo socio" else "Socio existente")
                                                append(" · solo registro")
                                            }
                                            if (idx >= 0) {
                                                rows[idx] = rows[idx].copy(ok = true, result = label)
                                            }
                                            outcomes += EnrollProcessOutcome(
                                                rowId = row.id,
                                                title = personLabel,
                                                subtitle = label,
                                                ok = true,
                                                clientId = client.id,
                                                orderId = null,
                                                amountCents = 0L,
                                                amountInput = amountInput,
                                                isCourtesy = false,
                                                paymentConfigured = false,
                                                paymentLines = emptyList(),
                                            )
                                            progress = (index + 1) to toProcess.size
                                            return@forEachIndexed
                                        }
                                        val tOrder = clock.markNow()
                                        // Orden ya creada en un intento anterior: no se vuelve a crear, solo se cobra.
                                        val priorOrderId = rows.getOrNull(idx)?.createdOrderId
                                        val orderStatus = when {
                                            isCourtesy -> AdminMembershipOrderStatus.COURTESY
                                            else -> AdminMembershipOrderStatus.PENDING
                                        }
                                        val primaryMethod = row.paymentLines.firstOrNull()?.method ?: row.payMethod
                                        val orderDate = parseFlexibleDateToIso(row.startsAt.trim())
                                            ?: row.startsAt.trim().ifBlank { bulkFormTodayIsoDate() }
                                        val periodCovered = priorOrderId == null && runCatching {
                                            AdminRepo.api.isMembershipPeriodCovered(
                                                clientId = client.id,
                                                planId = row.planId!!,
                                                orderDate = orderDate.take(10),
                                            )
                                        }.getOrDefault(false)
                                        if (periodCovered) {
                                            val plan = plans.firstOrNull { it.id == row.planId }
                                            val msg = if (plan != null) {
                                                duplicateMembershipPeriodMessage(plan.name, plan.billingPeriod)
                                            } else {
                                                "Periodo ya cubierto · no se crea orden duplicada"
                                            }
                                            if (idx >= 0) {
                                                rows[idx] = rows[idx].copy(ok = false, result = msg)
                                            }
                                            outcomes += EnrollProcessOutcome(
                                                rowId = row.id,
                                                title = personLabel,
                                                subtitle = msg,
                                                ok = false,
                                                clientId = client.id,
                                                orderId = null,
                                                amountCents = amountCents,
                                                amountInput = amountInput,
                                                isCourtesy = isCourtesy,
                                                paymentConfigured = false,
                                                paymentLines = emptyList(),
                                            )
                                            progress = (index + 1) to toProcess.size
                                            return@forEachIndexed
                                        }
                                        // Cobro de esta fila viaja con la orden: si el servidor la deja en aprobación,
                                        // el cobro queda en la solicitud y se aplica al aprobarla (no se pierde).
                                        val rowDueBs = bulkDueBsCents(amountCents, row.pendingBsCents)
                                        val rowCollect = if (!isCourtesy && row.paymentConfigured && row.paymentLines.isNotEmpty() &&
                                            (rowDueBs > 0L || row.paymentLines.any { it.method.usesUsdAmount() })
                                        ) {
                                            // La cobertura de la planilla ya se validó antes de crear órdenes (ver el botón).
                                            val (rowMerged, rowLinesBase) = prepareBulkCollectLines(
                                                entries = listOf(amountInput to row.paymentLines),
                                                fallbackAmountInput = amountInput,
                                                quote = exchangeQuote,
                                            )
                                            // Comprobantes: se guardan ya y se asocian al pago al aprobar la orden.
                                            val rowLines = rowLinesBase.mapIndexed { i, line ->
                                                val proof = rowMerged.getOrNull(i)?.proof
                                                if (proof == null) line else line.copy(proofPath = AdminRepo.api.stageMembershipOrderProof(proof))
                                            }
                                            val rowLineTotal = rowLines.sumOf { it.amountCents }.coerceAtLeast(0L)
                                            val rowHasUsd = rowLines.any { (it.foreignUsdCents ?: 0L) > 0L }
                                            AdminPaymentCollect(
                                                currency = GymlyCurrency.CODE,
                                                targets = listOf(
                                                    AdminPaymentCollectTarget(
                                                        clientId = client.id,
                                                        amountCents = bulkNetDueBsCents(amountCents, row.pendingBsCents, row.discount?.cents ?: 0L),
                                                        planId = row.planId,
                                                        branchId = companyId,
                                                        trainerId = row.trainerId,
                                                        discount = row.discount,
                                                    ),
                                                ),
                                                lines = rowLines,
                                                paidAt = paymentDateToPaidAtIso(row.paidAt.ifBlank { bulkFormTodayIsoDate() }),
                                                changeGivenCents = when {
                                                    rowLineTotal > 0L -> row.changeGivenCents.coerceIn(0L, rowLineTotal)
                                                    rowHasUsd -> row.changeGivenCents.coerceAtLeast(0L)
                                                    else -> 0L
                                                },
                                                changeGivenUsdCents = row.changeGivenUsdCents.coerceAtLeast(0L),
                                            )
                                        } else {
                                            null
                                        }
                                        val order = if (priorOrderId != null) null else AdminRepo.api.createMembershipOrder(
                                            AdminMembershipOrderUpsert(
                                                clientId = client.id,
                                                planId = row.planId!!,
                                                branchId = companyId,
                                                trainerId = row.trainerId,
                                                method = if (isCourtesy) null else primaryMethod,
                                                payerPhone = null,
                                                status = orderStatus,
                                                orderDate = orderDate,
                                                amountCents = amountCents,
                                                pendingUsdCents = if (isCourtesy) 0L else row.pendingUsdCents,
                                                pendingBsCents = if (isCourtesy) 0L else row.pendingBsCents,
                                                currency = GymlyCurrency.CODE,
                                                notes = "Alta masiva",
                                                collect = rowCollect,
                                            ),
                                        )
                                        msOrders += tOrder.elapsedNow().inWholeMilliseconds
                                        val orderId = order?.id ?: priorOrderId!!
                                        val orderLabel = order?.displayNumber() ?: rows[idx].createdOrderLabel.orEmpty()
                                        if (order != null && idx >= 0) {
                                            rows[idx] = rows[idx].copy(createdOrderId = orderId, createdOrderLabel = orderLabel)
                                        }
                                        val label = buildString {
                                            append(if (resolved.created) "Nuevo socio" else "Socio existente")
                                            append(" · orden $orderLabel")
                                        }
                                        if (idx >= 0) {
                                            rows[idx] = rows[idx].copy(ok = true, result = label)
                                        }
                                        outcomes += EnrollProcessOutcome(
                                            rowId = row.id,
                                            title = personLabel,
                                            subtitle = label,
                                            ok = true,
                                            clientId = client.id,
                                            orderId = orderId,
                                            amountCents = amountCents,
                                            amountInput = amountInput,
                                            isCourtesy = isCourtesy,
                                            paymentConfigured = row.paymentConfigured,
                                            paymentLines = row.paymentLines,
                                            changeGivenCents = row.changeGivenCents,
                                            changeGivenUsdCents = row.changeGivenUsdCents,
                                            pendingUsdCents = row.pendingUsdCents,
                                            pendingBsCents = row.pendingBsCents,
                                            discount = row.discount,
                                        )
                                    } catch (e: Throwable) {
                                        val pending = pendingApprovals.offer(e)
                                        val msg = if (pending) {
                                            "Enviada a aprobación"
                                        } else {
                                            e.userMessage("Error")
                                        }
                                        if (idx >= 0) {
                                            rows[idx] = rows[idx].copy(ok = pending, result = msg)
                                        }
                                        outcomes += EnrollProcessOutcome(
                                            rowId = row.id,
                                            title = personLabel,
                                            subtitle = msg,
                                            ok = pending,
                                            clientId = null,
                                            orderId = null,
                                            amountCents = amountCents,
                                            amountInput = amountInput,
                                            isCourtesy = isCourtesy,
                                            paymentConfigured = row.paymentConfigured,
                                            paymentLines = row.paymentLines,
                                        )
                                    }
                                    progress = (index + 1) to toProcess.size
                                }

                                var sharedMobileTip: String? = null
                                val chargeable = outcomes.filter {
                                    it.ok && !it.isCourtesy && it.paymentConfigured &&
                                        it.orderId != null && it.paymentLines.isNotEmpty() &&
                                        (
                                            bulkDueBsCents(it.amountCents, it.pendingBsCents) > 0L ||
                                                it.paymentLines.any { line -> line.method.usesUsdAmount() }
                                            )
                                }
                                // Cobro por fila: cada persona se cobra con sus propias líneas, vuelto y descuento.
                                // Resultado del cobro de una fila: reemplaza su estado anterior sin repetir mensajes.
                                fun marcarCobroFila(rowId: String?, ok: Boolean, detail: String) {
                                    val oi = outcomes.indexOfFirst { it.rowId == rowId }
                                    if (oi >= 0) {
                                        val prev = outcomes[oi]
                                        outcomes[oi] = prev.copy(ok = ok, subtitle = listOfNotNull(prev.subtitle, detail).joinToString(" · "))
                                    }
                                    val rIdx = rows.indexOfFirst { it.id == rowId }
                                    if (rIdx >= 0) {
                                        rows[rIdx] = rows[rIdx].copy(ok = ok, result = detail)
                                    }
                                }
                                chargeable.forEach { chargeableItem ->
                                    val chargeable = listOf(chargeableItem)
                                    if (chargeable.isNotEmpty()) {
                                        val tPay = clock.markNow()
                                        try {
                                            // Antes de cobrar: si la orden ya tiene cobros aplicados, no se vuelve a cobrar.
                                            val orderIdNow = chargeableItem.orderId ?: throw IllegalStateException("Orden sin id")
                                            if (AdminRepo.api.getMembershipOrder(orderIdNow).payments.isNotEmpty()) {
                                                marcarCobroFila(chargeableItem.rowId, true, "Orden creada · cobro ya registrado (no se cobra de nuevo)")
                                                return@forEach
                                            }
                                            val exchangeQuote = runCatching { AdminRepo.api.currentExchangeRate() }.getOrNull()
                                            val sharedAlerts = bulkSharedMobileRefAlerts(
                                                chargeable.map { item ->
                                                    item.title to item.paymentLines
                                                },
                                            )
                                            sharedMobileTip = sharedAlerts.joinToString(" · ").takeIf { it.isNotBlank() }
                                            val (mergedLines, collectLines) = prepareBulkCollectLines(
                                                entries = chargeable.map { item ->
                                                    item.amountInput to item.paymentLines
                                                },
                                                fallbackAmountInput = chargeable.first().amountInput,
                                                quote = exchangeQuote,
                                            )
                                            val dueBsTotal = chargeable.sumOf {
                                                bulkNetDueBsCents(it.amountCents, it.pendingBsCents, it.discount?.cents ?: 0L)
                                            }
                                            validateBulkCollectCoverage(dueBsTotal, collectLines)?.let { msg ->
                                                throw IllegalStateException(msg)
                                            }
                                            val targets = chargeable.map { item ->
                                                val dueBs = bulkNetDueBsCents(item.amountCents, item.pendingBsCents, item.discount?.cents ?: 0L)
                                                AdminPaymentCollectTarget(
                                                    clientId = item.clientId!!,
                                                    membershipOrderId = item.orderId,
                                                    amountCents = dueBs,
                                                    planId = toProcess.first { it.id == item.rowId }.planId,
                                                    branchId = companyId,
                                                    trainerId = toProcess.first { it.id == item.rowId }.trainerId,
                                                    // Cada persona lleva su propio descuento dentro del mismo cobro.
                                                    discount = item.discount,
                                                )
                                            }
                                            val lineTotalCents = collectLines.sumOf { it.amountCents }.coerceAtLeast(0L)
                                            val hasUsdLine = collectLines.any { (it.foreignUsdCents ?: 0L) > 0L }
                                            // Vuelto una sola vez en la colección (primera fila que lo indicó).
                                            val requestedChange = bulkSingleChangeGivenCents(
                                                chargeable.map { it.changeGivenCents },
                                            )
                                            val changeGivenCents = when {
                                                lineTotalCents > 0L -> requestedChange.coerceIn(0L, lineTotalCents)
                                                hasUsdLine -> requestedChange.coerceAtLeast(0L)
                                                else -> 0L
                                            }
                                            val changeGivenUsdCents = bulkSingleChangeGivenUsdCents(
                                                chargeable.map { it.changeGivenUsdCents },
                                            )
                                            val payments = AdminRepo.api.collectPayments(
                                                AdminPaymentCollect(
                                                    currency = GymlyCurrency.CODE,
                                                    targets = targets,
                                                    lines = collectLines,
                                                    paidAt = paymentDateToPaidAtIso(
                                                        chargeable.firstOrNull()?.rowId
                                                            ?.let { id -> toProcess.firstOrNull { it.id == id }?.paidAt }
                                                            ?.takeIf { it.isNotBlank() }
                                                            ?: bulkFormTodayIsoDate(),
                                                    ),
                                                    changeGivenCents = changeGivenCents,
                                                    changeGivenUsdCents = changeGivenUsdCents,
                                                ),
                                            )
                                            mergedLines.forEachIndexed { lineIndex, line ->
                                                line.proof?.let { proof ->
                                                    payments.getOrNull(lineIndex)?.let { payment ->
                                                        AdminRepo.api.uploadPaymentProof(payment.id, proof)
                                                    }
                                                }
                                            }
                                        } catch (e: Throwable) {
                                            // Cobro con descuento: queda en Aprobaciones. No es un error; cuenta como correcto.
                                            if (pendingApprovals.offer(e)) {
                                                marcarCobroFila(chargeableItem.rowId, true, "Orden creada · cobro enviado a aprobación")
                                            } else {
                                                // La respuesta puede fallar aunque el servidor haya aplicado el cobro: se consulta antes.
                                                val aplicado = chargeableItem.orderId?.let { id ->
                                                    runCatching { AdminRepo.api.getMembershipOrder(id).payments.isNotEmpty() }.getOrDefault(false)
                                                } ?: false
                                                if (aplicado) {
                                                    marcarCobroFila(chargeableItem.rowId, true, "Orden creada · cobro aplicado (confirmado por el servidor)")
                                                } else {
                                                    val msg = e.userMessage("Error al registrar cobros combinados")
                                                    error = msg
                                                    marcarCobroFila(chargeableItem.rowId, false, "Orden creada · cobro no aplicado: $msg")
                                                }
                                            }
                                        }
                                        msPayments += tPay.elapsedNow().inWholeMilliseconds
                                    }
                                }

                                val okCount = outcomes.count { it.ok }
                                val failCount = outcomes.size - okCount
                                val dupCount = outcomes.count {
                                    !it.ok && it.subtitle.orEmpty().contains("duplicado", ignoreCase = true)
                                }
                                val totalMs = started.elapsedNow().inWholeMilliseconds
                                fun secLabel(ms: Long): String {
                                    val whole = ms / 1000
                                    val tenths = (ms % 1000) / 100
                                    return "$whole.${tenths}s"
                                }
                                val timingHint = buildString {
                                    append(secLabel(totalMs))
                                    append(" (altas ${secLabel(msResolveCreate + msOrders)}")
                                    if (msPhoto > 0) append(" · fotos ${secLabel(msPhoto)}")
                                    if (msPayments > 0) append(" · cobros ${secLabel(msPayments)}")
                                    append(")")
                                }
                                val dupHint = if (dupCount > 0) " · $dupCount plan/periodo duplicado(s)" else ""
                                val mobileHint = sharedMobileTip?.let { " · $it" }.orEmpty()
                                processSummary = "$okCount correctos · $failCount con error$dupHint · $timingHint$mobileHint"
                                if (dupCount > 0) {
                                    error = "Hay $dupCount persona(s) con plan/periodo duplicado. " +
                                        "No se puede cobrar el mismo plan dos veces en el mismo periodo."
                                }
                                processResults = outcomes.map {
                                    BulkProcessResultItem(
                                        title = it.title,
                                        subtitle = it.subtitle,
                                        ok = it.ok,
                                        rowId = it.rowId,
                                        // Filas fallidas: al corregir solo se reintenta lo que falta (cliente y orden ya creados se reutilizan).
                                        keepForCorrection = !it.ok,
                                    )
                                }
                                processing = false

                                // Sync acceso en segundo plano (Hikvision): no bloquea el resultado.
                                if (accessSyncIds.isNotEmpty()) {
                                    launch {
                                        accessSyncIds.forEach { clientId ->
                                            runCatching { AdminRepo.api.syncClientAccess(clientId) }
                                        }
                                    }
                                }
                            }
                        },
                        enabled = !processing && !loadingMeta && missingBulkPermissions.isEmpty(),
                        modifier = if (compact) Modifier.fillMaxWidth() else Modifier,
                        shape = RoundedCornerShape(10.dp),
                        colors = ButtonDefaults.buttonColors(
                            containerColor = AdminColors.Primary,
                            contentColor = AdminColors.OnBrand,
                        ),
                    ) {
                        Text(
                            if (processing) "Procesando…" else if (missingBulkPermissions.isNotEmpty()) "Falta permiso: ${missingBulkPermissions.joinToString(", ")}" else "Procesar planilla ($validRows)",
                            fontWeight = FontWeight.SemiBold,
                        )
                    }
                }

            Spacer(Modifier.height(12.dp))
            error?.let { Text(it, color = AdminColors.Error, fontSize = 13.sp) }
            if (processing) {
                Spacer(Modifier.height(8.dp))
                val (done, total) = progress
                LinearProgressIndicator(
                    progress = { if (total == 0) 0f else done.toFloat() / total },
                    modifier = Modifier.fillMaxWidth(),
                    color = AdminColors.Primary,
                )
                Text("$done / $total", color = AdminColors.TextMuted, fontSize = 12.sp)
            }

            Spacer(Modifier.height(12.dp))
            VerticalScrollColumn(
                Modifier
                    .weight(1f)
                    .fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(14.dp),
            ) {
                if (loadingMeta) {
                    Box(Modifier.fillMaxWidth().padding(24.dp), contentAlignment = Alignment.Center) {
                        CircularProgressIndicator(color = AdminColors.Primary)
                    }
                }

                SectionCard(
                    title = "Qué registrar en esta planilla",
                    hint = "Inscribirse = socio nuevo, con membresía o solo ficha. Pagos = socio existente, solo orden/cobro.",
                ) {
                    Row(
                        Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(10.dp),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        ModeChip(
                            label = "Inscribirse",
                            selected = mode == BulkSignupMode.Enroll,
                            onClick = { mode = BulkSignupMode.Enroll },
                        )
                        ModeChip(
                            label = "Pagos",
                            selected = mode == BulkSignupMode.Payment,
                            onClick = { mode = BulkSignupMode.Payment },
                        )
                    }
                    Spacer(Modifier.height(12.dp))
                    Text(
                        if (enrollMembership) {
                            "Captura la foto primero. Cada tarjeta es una persona; el cobro admite varios métodos (efectivo, móvil, divisas…)."
                        } else {
                            "Busca socios, agrega a la lista y configura un cobro único (uno o varios métodos) para varios socios a la vez."
                        },
                        color = AdminColors.TextMuted,
                        fontSize = 12.sp,
                    )
                }

                if (mode == BulkSignupMode.Payment) {
                    BulkPaymentOnlyPanel(
                        clients = existingClients,
                        plans = plans,
                        trainers = trainers,
                        companyId = companyId,
                        processing = processing,
                        onProcessingChange = { processing = it },
                        onProgress = { done, total -> progress = done to total },
                        onDoneMessage = { /* handled via result dialog */ },
                        onError = { error = it },
                    )
                } else if (enrollMembership) {
                    SectionCard(
                        title = "Planilla",
                        hint = "Una tarjeta = una persona. La foto se muestra en grande al capturarla o subirla.",
                    ) {
                        // Celular (ancho < 600 dp): resumen arriba y botones abajo, con área táctil cómoda.
                        BoxWithConstraints(Modifier.fillMaxWidth()) {
                        if (maxWidth < 600.dp) {
                            Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(10.dp)) {
                                Text(
                                    "$validRows personas · a cobrar ${formatMoney(totalCents.toInt(), GymlyCurrency.CODE)}",
                                    color = AdminColors.TextMuted,
                                    fontSize = 13.sp,
                                )
                                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                    OutlinedButton(
                                        onClick = { rows.add(newBulkMemberRow(plans, defaultPlanId)) },
                                        enabled = !processing,
                                        modifier = Modifier.weight(1f).heightIn(min = 48.dp),
                                        shape = RoundedCornerShape(10.dp),
                                    ) { Text("+ Persona", color = AdminColors.Primary) }
                                    OutlinedButton(
                                        onClick = { repeat(5) { rows.add(newBulkMemberRow(plans, defaultPlanId)) } },
                                        enabled = !processing,
                                        modifier = Modifier.weight(1f).heightIn(min = 48.dp),
                                        shape = RoundedCornerShape(10.dp),
                                    ) { Text("+5", color = AdminColors.Primary) }
                                }
                            }
                        } else {
                        Row(
                            Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            Text(
                                "$validRows personas · a cobrar ${formatMoney(totalCents.toInt(), GymlyCurrency.CODE)}",
                                color = AdminColors.TextMuted,
                                fontSize = 13.sp,
                            )
                            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                OutlinedButton(
                                    onClick = { rows.add(newBulkMemberRow(plans, defaultPlanId)) },
                                    enabled = !processing,
                                    shape = RoundedCornerShape(10.dp),
                                ) { Text("+ Persona", color = AdminColors.Primary) }
                                OutlinedButton(
                                    onClick = { repeat(5) { rows.add(newBulkMemberRow(plans, defaultPlanId)) } },
                                    enabled = !processing,
                                    shape = RoundedCornerShape(10.dp),
                                ) { Text("+5", color = AdminColors.Primary) }
                            }
                        }
                        }
                        }
                        Spacer(Modifier.height(14.dp))
                        Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                            rows.forEachIndexed { index, row ->
                                val excludeIds = rows
                                    .mapNotNull { it.linkedClientId }
                                    .filter { it != row.linkedClientId }
                                    .toSet()
                                BulkEnrollMemberCard(
                                    index = index,
                                    row = row,
                                    plans = plans,
                                    trainers = trainers,
                                    existingClients = existingClients,
                                    excludeClientIds = excludeIds,
                                    siblingRows = rows.filter { it.id != row.id },
                                    enabled = !processing,
                                    canRemove = rows.size > 1,
                                    onRowChange = { rows[index] = it },
                                    onUploadPhoto = {
                                        openImagePicker { picked ->
                                            if (picked != null) {
                                                rows[index] = row.copy(
                                                    photo = PickedImagePayload(picked.bytes, picked.fileName, picked.mimeType),
                                                    photoName = picked.fileName,
                                                    result = null,
                                                    ok = null,
                                                )
                                            }
                                        }
                                    },
                                    onCapturePhoto = { bulkTerminalRowIndex = index },
                                    onClearPhoto = {
                                        rows[index] = row.copy(photo = null, photoName = null, result = null, ok = null)
                                    },
                                    onOpenPayment = { paymentDialogRowIndex = index },
                                    onClearPayment = {
                                        rows[index] = row.copy(
                                            paymentConfigured = false,
                                            paymentStatus = AdminMembershipOrderStatus.PENDING,
                                            payMethod = null,
                                            payerPhone = "",
                                            paymentLines = emptyList(),
                                            result = null,
                                            ok = null,
                                        )
                                    },
                                    onRemove = { rows.removeAt(index) },
                                )
                            }
                        }
                    }
                }
            }
        }
    }

    bulkTerminalRowIndex?.let { rowIndex ->
        TerminalPhotoCaptureDialog(
            onDismiss = { bulkTerminalRowIndex = null },
            onCaptured = { payload ->
                if (rowIndex in rows.indices) {
                    val row = rows[rowIndex]
                    rows[rowIndex] = row.copy(
                        photo = payload,
                        photoName = payload.fileName,
                        result = null,
                        ok = null,
                    )
                }
                bulkTerminalRowIndex = null
            },
        )
    }

    paymentDialogRowIndex?.let { rowIndex ->
        if (rowIndex in rows.indices) {
            val row = rows[rowIndex]
            val personLabel = listOf(row.firstName, row.lastName)
                .map { it.trim() }
                .filter { it.isNotEmpty() }
                .joinToString(" ")
                .ifBlank { row.email.ifBlank { "Persona ${rowIndex + 1}" } }
            val planillaRefs = rows.flatMapIndexed { idx, r ->
                if (idx == rowIndex) emptyList()
                else r.paymentLines.mapNotNull { line ->
                    if (line.method != AdminPaymentMethod.MOBILE_PAYMENT) null
                    else paymentReferenceLookupKey(line.method, line.notes).takeIf { it.length >= 5 }
                }
            }.toSet()
            BulkRowPaymentDialog(
                personLabel = personLabel,
                defaultAmount = planDefaultAmount(plans, row.planId),
                defaultUsdAmount = planDefaultUsdAmount(plans, row.planId),
                initialStatus = row.paymentStatus,
                initialMethod = row.payMethod,
                initialAmount = row.amountOverride.ifBlank { planDefaultAmount(plans, row.planId) },
                initialPayerPhone = row.payerPhone,
                initialLines = row.paymentLines,
                initialChangeGivenCents = row.changeGivenCents,
                initialChangeGivenUsdCents = row.changeGivenUsdCents,
                initialPendingUsd = row.pendingUsdCents.takeIf { it > 0 }?.let { centsToAmountInput(it) }.orEmpty(),
                initialPendingBs = row.pendingBsCents.takeIf { it > 0 }?.let { centsToAmountInput(it) }.orEmpty(),
                initialDiscount = row.discount,
                extraKnownRefs = planillaRefs,
                checkExistingMobilePayments = true,
                paymentDateIso = row.paidAt.ifBlank { bulkFormTodayIsoDate() },
                allowDiscount = true,
                onDismiss = { paymentDialogRowIndex = null },
                onConfirmResult = { result ->
                    val planAmount = planDefaultAmount(plans, row.planId)
                    rows[rowIndex] = row.copy(
                        kind = BulkEnrollKind.MEMBERSHIP,
                        paymentConfigured = true,
                        paymentStatus = result.status,
                        payMethod = result.method,
                        payerPhone = result.payerPhone.orEmpty(),
                        paymentLines = result.lines,
                        discount = result.discount,
                        changeGivenCents = result.changeGivenCents,
                        changeGivenUsdCents = result.changeGivenUsdCents,
                        pendingUsdCents = result.pendingUsdCents,
                        pendingBsCents = result.pendingBsCents,
                        // Monto del plan en Bs (principal); no mezclar $ en este campo.
                        amountOverride = if (result.status == AdminMembershipOrderStatus.COURTESY) {
                            ""
                        } else {
                            row.amountOverride.ifBlank { planAmount }
                        },
                        result = null,
                        ok = null,
                    )
                    paymentDialogRowIndex = null
                },
            )
        }
    }

    processResults?.let { items ->
        val hasErrors = items.any { !it.ok }
        BulkProcessResultDialog(
            title = if (hasErrors) "Planilla con errores" else "Planilla procesada",
            summary = processSummary,
            items = items,
            primaryLabel = if (hasErrors) "Corregir planilla" else "Nueva planilla",
            secondaryLabel = if (hasErrors) "Nueva planilla" else null,
            onPrimary = {
                processResults = null
                if (hasErrors) keepOnlyFailedEnrollRows(items) else resetEnrollRows()
            },
            onSecondary = {
                processResults = null
                resetEnrollRows()
            },
        )
    }
}

@Composable
private fun SectionCard(
    title: String,
    hint: String? = null,
    content: @Composable () -> Unit,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        Text(title, fontWeight = FontWeight.Bold, fontSize = 15.sp, color = AdminColors.Text)
        if (!hint.isNullOrBlank()) {
            Text(
                hint,
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
                modifier = Modifier.padding(top = 4.dp, bottom = 4.dp),
            )
        }
        Spacer(Modifier.height(12.dp))
        content()
    }
}

@Composable
private fun ModeChip(label: String, selected: Boolean, onClick: () -> Unit) {
    val bg = if (selected) AdminColors.Primary else AdminColors.Outline.copy(alpha = 0.35f)
    val fg = if (selected) AdminColors.OnBrand else AdminColors.TextMuted
    Text(
        label,
        modifier = Modifier
            .clip(RoundedCornerShape(20.dp))
            .background(bg)
            .border(
                width = if (selected) 0.dp else 1.dp,
                color = if (selected) AdminColors.Primary else AdminColors.Outline,
                shape = RoundedCornerShape(20.dp),
            )
            .clickable(onClick = onClick)
            .padding(horizontal = 14.dp, vertical = 8.dp),
        color = fg,
        fontWeight = FontWeight.SemiBold,
        fontSize = 13.sp,
    )
}

private data class ResolvedClient(val client: AdminClient, val created: Boolean)

private fun rowHasPersonData(row: BulkMemberRow): Boolean {
    val hasName = row.firstName.trim().isNotEmpty() && row.lastName.trim().isNotEmpty()
    val hasEmail = row.email.trim().isNotEmpty()
    return hasName || hasEmail
}

private suspend fun resolveOrCreateClient(
    row: BulkMemberRow,
    existing: List<AdminClient>,
    companyId: String?,
    allowCreate: Boolean,
): ResolvedClient {
    row.linkedClientId?.let { linkedId ->
        existing.firstOrNull { it.id == linkedId }?.let { return ResolvedClient(it, created = false) }
    }
    val email = row.email.trim()
    if (email.isNotEmpty()) {
        val match = existing.firstOrNull { it.email?.trim()?.equals(email, ignoreCase = true) == true }
        if (match != null) return ResolvedClient(match, created = false)
    }
    val doc = row.docId.trim()
    if (doc.isNotEmpty()) {
        val match = existing.firstOrNull { it.docId?.trim()?.equals(doc, ignoreCase = true) == true }
        if (match != null) return ResolvedClient(match, created = false)
    }
    val first = row.firstName.trim()
    val last = row.lastName.trim()
    if (!allowCreate || first.isEmpty() || last.isEmpty()) {
        throw IllegalStateException(
            "Socio no encontrado. Elige uno de las sugerencias, usa correo/documento de un socio existente, " +
                "o nombre y apellido para crear uno nuevo.",
        )
    }
    val sex = row.sex
    val address = row.address.trim()
    val created = AdminRepo.api.createClient(
        AdminClientUpsert(
            firstName = first,
            lastName = last,
            email = email.ifBlank { null },
            phone = row.phone.trim().ifBlank { null },
            docId = doc.ifBlank { null },
            sex = sex,
            address = address,
            status = AdminClientStatus.ACTIVE,
            notes = "Alta masiva",
            branchId = companyId,
        ),
    )
    return ResolvedClient(created, created = true)
}
